import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { CheckCircle2, Award, Beaker, FlaskConical } from 'lucide-react';
import { ROUTE_PATHS } from '@/lib/index';
import { companyStatsData } from '@/data/index';
import { StatCard } from '@/components/Cards';
import { IMAGES } from '@/assets/images';

const testStandards = [
  'ISO 37 拉伸强度与断裂伸长率测试',
  'ISO 48 橡胶硬度测定（IRHD）',
  'ISO 815 压缩永久变形测试',
  'ISO 1817 液体浸泡测试',
  'ISO 1431 耐臭氧老化测试',
  'ISO 4589 / UL 94 燃烧性能测试',
  'ASTM D412 / D2240 拉伸与硬度',
  'GB/T 3452 密封件外观与尺寸',
];

const certifications = [
  'ISO 9001:2015 质量管理体系认证',
  'CE 合规认证（出口欧盟）',
  'RoHS 有害物质限制指令',
  'REACH 化学品合规',
  'SGS 第三方检测认可',
];

const equipmentList = [
  { name: '拉力试验机', desc: '精度 ±0.5%，量程 50kN，符合 ISO 37 / ASTM D412' },
  { name: '邵氏硬度计 A/D 型', desc: '数字式，精度 ±0.5 IRHD，符合 ISO 48 / ASTM D2240' },
  { name: '老化试验箱', desc: '温度范围 -70°C ～ +250°C，符合 ISO 188 热空气老化' },
  { name: '耐臭氧试验箱', desc: '臭氧浓度 0～500 pphm，符合 ISO 1431' },
  { name: '低温脆性试验机', desc: '最低 -80°C，符合 ISO 812' },
];

export default function About() {
  return (
    <div className="min-h-screen bg-background">
      <section className="relative h-[50vh] min-h-[400px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src={IMAGES.FACTORY_1}
            alt="工厂背景"
            className="w-full h-full object-cover opacity-30 img-industrial"
          />
          <div className="hero-overlay" />
        </div>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="relative z-10 text-center px-4"
        >
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="w-12 h-0.5 bg-primary" />
            <span className="font-mono text-xs font-bold tracking-widest text-primary uppercase">ABOUT US</span>
            <div className="w-12 h-0.5 bg-primary" />
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-4">关于博元合成</h1>
          <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto">
            专注工业橡胶密封材料研发与制造，二十余年品质坚守
          </p>
        </motion.div>
      </section>

      <section className="py-24 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="w-1 h-8 bg-primary" />
              <h2 className="text-3xl font-bold">公司简介</h2>
              </div>
              <div className="space-y-4 text-muted-foreground leading-relaxed">
                <p>
                  博元合成（北京）工业科技有限公司成立于2003年，总部位于北京市朝阳区，是一家专注于特种工业橡胶板材研发、制造与销售的高新技术企业。
                </p>
                <p>
                  公司拥有完整的生产线和先进的检测设备，年产能超过5000吨。产品广泛应用于化工石化、建筑幕墙、汽车轨道交通、矿山采掘、电力能源、电子半导体、食品医疗、航空航天等领域，出口至欧盟、美国、澳大利亚、日本、韩国等10个国家和地区。
                </p>
              </div>

              <div className="mt-8">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-1 h-6 bg-primary" />
                  <h3 className="text-xl font-bold">研发与技术积累</h3>
                </div>
                <p className="text-muted-foreground leading-relaxed">
                  拥有资深橡胶配方工程师团队，建有国内一流的橡胶材料检测实验室。在FKM、EPDM、NBR、NR、CR、ESD等多种特种橡胶领域积累了丰富的配方开发经验，可提供定制化材料解决方案。
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="grid grid-cols-2 gap-4"
            >
              {companyStatsData.map((stat) => (
                <StatCard key={stat.id} stat={stat} />
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-24 px-4 bg-section-alt">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="w-12 h-0.5 bg-primary" />
              <span className="font-mono text-xs font-bold tracking-widest text-primary uppercase">QUALITY ASSURANCE</span>
              <div className="w-12 h-0.5 bg-primary" />
            </div>
            <h2 className="text-3xl md:text-4xl font-bold mb-4">质量保障体系</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              三段式全程质量管控：原料检验 → 过程控制 → 出厂检测，确保每批产品符合国际标准
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="flex justify-center mb-12"
          >
            <div className="card-industrial p-8 inline-flex items-center gap-4">
              <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
                <Award className="w-8 h-8 text-primary" />
              </div>
              <div>
                <div className="text-2xl font-bold mb-1">ISO 9001:2015</div>
                <div className="text-sm text-muted-foreground">国际质量管理体系认证</div>
              </div>
            </div>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="card-industrial p-6"
            >
              <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                  <Beaker className="w-5 h-5 text-primary" />
                  检测标准
              </h3>
              <ul className="space-y-3">
                {testStandards.map((standard, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                    <span className="text-muted-foreground">{standard}</span>
                  </li>
                ))}
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="card-industrial p-6"
            >
              <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                  <Award className="w-5 h-5 text-primary" />
                  资质认证
              </h3>
              <ul className="space-y-3">
                {certifications.map((cert, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                    <span className="text-muted-foreground">{cert}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-24 px-4">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="w-12 h-0.5 bg-primary" />
              <span className="font-mono text-xs font-bold tracking-widest text-primary uppercase">LAB EQUIPMENT</span>
              <div className="w-12 h-0.5 bg-primary" />
            </div>
            <h2 className="text-3xl md:text-4xl font-bold mb-4">实验室检测设备</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              配备符合ISO/ASTM国际标准的专业橡胶检测设备，出厂产品均经严格测试
            </p>
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-8 items-center">
            <div className="grid gap-4">
              {equipmentList.map((equipment, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="card-industrial p-6 flex items-center gap-4"
                >
                  <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <FlaskConical className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <div className="font-bold mb-1">{equipment.name}</div>
                    <div className="text-sm text-muted-foreground font-mono">{equipment.desc}</div>
                  </div>
                </motion.div>
              ))}
            </div>

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="relative h-[400px] rounded-lg overflow-hidden"
            >
              <img
                src={IMAGES.QUALITY_LAB_5}
                alt="质量检测实验室"
                className="w-full h-full object-cover img-industrial"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-background/80 to-transparent" />
              <div className="absolute bottom-6 left-6 right-6">
                <div className="badge-code mb-2">QUALITY LAB</div>
                <div className="text-lg font-bold">质量检测实验室</div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-16 px-4 bg-section-alt">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="max-w-4xl mx-auto text-center"
        >
          <h2 className="text-2xl md:text-3xl font-bold mb-6">
            了解更多产品信息或获取技术支持
          </h2>
          <Link
            to={ROUTE_PATHS.CONTACT}
            className="inline-flex items-center justify-center px-8 py-4 btn-primary-industrial rounded-lg text-lg"
          >
            联系我们
          </Link>
        </motion.div>
      </section>
    </div>
  );
}